import type { ChatRequestSetting, ChatSettings as Settings } from '@mogzo/shared';
import { Ban, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { fetchChatSettings, saveChatSettings, unblockPlayer } from '../../lib/chat';
import { Avatar } from '../community/Avatar';
import { Toggle } from '../notifications/NotificationSettings';
import { Alert } from '../ui';
import { Say } from './parts';

const cardClass = 'rounded-[18px] border border-line bg-card';

const WHO: { value: ChatRequestSetting; label: string; hint: string }[] = [
  { value: 'everyone', label: 'Everyone', hint: 'Anyone on MOGZO who’s 18 or over.' },
  {
    value: 'played',
    label: 'Players you’ve played with',
    hint: 'Only people you’ve been in a match or room with.',
  },
  {
    value: 'nobody',
    label: 'Nobody',
    hint: 'No new requests. You can still start a chat yourself.',
  },
];

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * Account → Chat (M38): who can send you requests, hiding swearing, read marks, and the players
 * you blocked.
 */
export function ChatSettings() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchChatSettings()
      .then(setSettings)
      .catch(() => setError('Couldn’t load your settings. Reload the page to try again.'));
  }, []);

  async function save(change: Partial<Settings>, call: () => Promise<unknown>) {
    if (!settings) return;
    setSettings({ ...settings, ...change });
    setBusy(true);
    setError(null);
    try {
      await call();
    } catch {
      setError('Couldn’t save that. Try again in a moment.');
      setSettings(await fetchChatSettings().catch(() => settings));
    } finally {
      setBusy(false);
    }
  }

  if (!settings) {
    return error ? (
      <Alert>{error}</Alert>
    ) : (
      <p role="status" className="py-6 text-center text-sm text-muted">
        Loading…
      </p>
    );
  }

  return (
    <>
      {error && <Alert>{error}</Alert>}
      {settings.off && (
        <Say tone="warning" icon={Ban}>
          {settings.off.until
            ? `Your chat is off until ${day(settings.off.until)}.`
            : 'Your chat is off until staff turn it back on.'}{' '}
          You can still read your chats. If you think it’s a mistake, write to hello@mogzo.com.
        </Say>
      )}
      <section aria-labelledby="who-heading" className={`${cardClass} p-5 sm:p-6`}>
        <h2 id="who-heading" className="text-lg leading-6 font-bold">
          Who can send you requests
        </h2>
        <p className="mt-1 mb-3.5 text-sm text-muted">
          A first message from someone new waits in Requests until you accept it. People you already
          chat with can always message you, unless you block them.
        </p>
        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="sr-only">Who can send you requests</legend>
          {WHO.map((option) => (
            <label
              key={option.value}
              className={`flex cursor-pointer items-start gap-3 rounded-[14px] border px-4 py-3.5 ${settings.requests === option.value ? 'border-ink shadow-[0_0_0_3px_var(--color-cream)]' : 'border-line'}`}
            >
              <input
                type="radio"
                name="requests"
                checked={settings.requests === option.value}
                disabled={busy}
                onChange={() =>
                  save({ requests: option.value }, () =>
                    saveChatSettings({ requests: option.value }),
                  )
                }
                className="mt-0.5 size-5 shrink-0 accent-ink"
              />
              <span>
                <b className="block font-semibold">{option.label}</b>
                <span className="block text-sm text-muted">{option.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
      </section>

      <section aria-label="Chat options" className={cardClass}>
        <Toggle
          name="Hide swearing"
          checked={settings.hideSwearing}
          busy={busy}
          onChange={(on) =>
            save({ hideSwearing: on }, () => saveChatSettings({ hideSwearing: on }))
          }
        >
          Swear words show as ****. Slurs and threats are never sent.
        </Toggle>
        <Toggle
          name="Show when you’ve read messages"
          checked={settings.readMarks}
          busy={busy}
          onChange={(on) => save({ readMarks: on }, () => saveChatSettings({ readMarks: on }))}
        >
          If it’s off, you won’t see when others have read yours either.
        </Toggle>
      </section>

      <section aria-labelledby="blocked-heading" className={`${cardClass} p-5 sm:p-6`}>
        <h2 id="blocked-heading" className="text-lg leading-6 font-bold">
          Blocked
        </h2>
        <p className="mt-1 text-sm text-muted">
          They can’t message you, send you requests or add you to groups. They aren’t told.
        </p>
        {settings.blocked.length ? (
          <ul className="m-0 mt-2 flex list-none flex-col p-0">
            {settings.blocked.map((person) => (
              <li key={person.username} className="flex min-h-14 items-center gap-3">
                <Avatar name={person.username} />
                <span className="min-w-0 flex-1 leading-[18px]">
                  <b className="block truncate font-semibold">{person.username}</b>
                  <span className="text-[13px] text-muted">Blocked {day(person.at)}</span>
                </span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    save(
                      { blocked: settings.blocked.filter((b) => b.username !== person.username) },
                      () => unblockPlayer(person.username),
                    )
                  }
                  className="rounded-full border border-line px-3.5 py-1.5 text-sm font-bold hover:border-muted"
                >
                  Unblock
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-muted">You haven’t blocked anyone.</p>
        )}
      </section>

      <section className="flex items-start gap-3.5 rounded-[18px] bg-field px-5 py-4.5">
        <Lock size={22} strokeWidth={1.9} aria-hidden="true" className="mt-0.5 shrink-0" />
        <div>
          <h2 className="font-semibold">How chat is kept safe</h2>
          <p className="mt-0.5 text-sm leading-[21px] text-muted">
            Messages are encrypted on the way and where they’re kept. MOGZO checks each one for
            slurs and threats before it’s sent. Staff read a chat only when someone in it reports a
            message. Chat is for players 18 and over.
          </p>
        </div>
      </section>
    </>
  );
}
